import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGroupLeaderPermission } from '@/components/hooks/useGroupLeaderPermission';
import { usePermissions } from '@/components/hooks/usePermissions';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Users, Plus, Edit, Trash2, Search, Music2, Mic } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function IntegrantesLouvor() {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    voices: [],
    instruments: [],
    member_id: '',
    member_name: ''
  });

  const queryClient = useQueryClient();

  const { canCreate, canEdit, canDelete, isSuperAdmin } = usePermissions();
  const { canManageWorship } = useGroupLeaderPermission();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['worshipMembers'],
    queryFn: () => base44.entities.WorshipTeamMember.list('-created_date')
  });

  const { data: churchMembers = [] } = useQuery({
    queryKey: ['churchMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });

  const sortedChurchMembers = useMemo(
    () => [...churchMembers].sort((a, b) =>
      String(a.full_name || '').localeCompare(String(b.full_name || ''), 'pt-BR', { sensitivity: 'base' })
    ),
    [churchMembers]
  );

  const hasCreatePermission = isSuperAdmin || canCreate('WorshipTeamMember') || canManageWorship;
  const hasEditPermission = isSuperAdmin || canEdit('WorshipTeamMember') || canManageWorship;
  const hasDeletePermission = isSuperAdmin || canDelete('WorshipTeamMember') || canManageWorship;

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.WorshipTeamMember.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipMembers'] });
      toast.success('Integrante cadastrado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.WorshipTeamMember.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipMembers'] });
      toast.success('Integrante atualizado com sucesso!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.WorshipTeamMember.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipMembers'] });
      toast.success('Integrante excluído com sucesso!');
    }
  });

  const voiceOptions = ['Soprano', 'Contralto', 'Tenor', 'Barítono'];
  const instrumentOptions = ['Bateria', 'Baixo', 'Guitarra', 'Violão', 'Teclado'];

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone: '',
      email: '',
      voices: [],
      instruments: [],
      member_id: '',
      member_name: ''
    });
    setEditingMember(null);
    setShowForm(false);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name || '',
      phone: member.phone || '',
      email: member.email || '',
      voices: member.voices || [],
      instruments: member.instruments || [],
      member_id: member.member_id || '',
      member_name: member.member_name || ''
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.full_name) {
      toast.error('O nome é obrigatório.');
      return;
    }

    if (editingMember) {
      updateMutation.mutate({ id: editingMember.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const toggleVoice = (voice) => {
    const updated = formData.voices.includes(voice)
      ? formData.voices.filter(v => v !== voice)
      : [...formData.voices, voice];
    setFormData({ ...formData, voices: updated });
  };

  const toggleInstrument = (instrument) => {
    const updated = formData.instruments.includes(instrument)
      ? formData.instruments.filter(i => i !== instrument)
      : [...formData.instruments, instrument];
    setFormData({ ...formData, instruments: updated });
  };

  const filteredMembers = members.filter(member =>
    member.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    member.email?.toLowerCase().includes(search.toLowerCase())
  );

  const churchMemberPhotoById = useMemo(() => {
    return new Map((churchMembers || []).map((m) => [m.id, m.photo_url]));
  }, [churchMembers]);
  const selectedChurchMember = useMemo(
    () => churchMembers.find((m) => m.id === formData.member_id),
    [churchMembers, formData.member_id]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Integrantes" 
            subtitle={`${members.length} na equipe`}
            showBack={true}
            action={
              hasCreatePermission && (
                <Button onClick={() => setShowForm(true)} size="sm" className="bg-white text-blue-700 hover:bg-blue-50">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              )
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar integrante"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Members List */}
        {filteredMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum integrante encontrado"
            description="Comece adicionando membros à equipe"
            actionLabel={hasCreatePermission ? "Adicionar Integrante" : undefined}
            onAction={hasCreatePermission ? () => setShowForm(true) : undefined}
          />
        ) : (
          <div className="space-y-3">
            {filteredMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {churchMemberPhotoById.get(member.member_id) ? (
                      <img
                        src={churchMemberPhotoById.get(member.member_id)}
                        alt={member.full_name}
                        className="w-11 h-11 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center font-semibold flex-shrink-0">
                        {member.full_name?.charAt(0)?.toUpperCase() || 'I'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900">{member.full_name}</h3>
                    </div>
                  </div>
                  {(hasEditPermission || hasDeletePermission) && (
                    <div className="flex gap-1">
                      {hasEditPermission && (
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(member)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {hasDeletePermission && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-red-600"
                          onClick={() => {
                            if (confirm('Deseja excluir este integrante?')) {
                              deleteMutation.mutate(member.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  {member.voices?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mic className="w-4 h-4 text-blue-500" />
                      {member.voices.map((voice, i) => (
                        <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-700">
                          {voice}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {member.instruments?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Music2 className="w-4 h-4 text-purple-500" />
                      {member.instruments.map((instrument, i) => (
                        <Badge key={i} variant="secondary" className="bg-purple-50 text-purple-700">
                          {instrument}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Editar Integrante' : 'Novo Integrante'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="mb-3 block">Vozes</Label>
              <div className="space-y-2">
                {voiceOptions.map(voice => (
                  <div key={voice} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.voices.includes(voice)}
                      onCheckedChange={() => toggleVoice(voice)}
                    />
                    <label className="text-sm cursor-pointer" onClick={() => toggleVoice(voice)}>
                      {voice}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-3 block">Instrumentos</Label>
              <div className="space-y-2">
                {instrumentOptions.map(instrument => (
                  <div key={instrument} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.instruments.includes(instrument)}
                      onCheckedChange={() => toggleInstrument(instrument)}
                    />
                    <label className="text-sm cursor-pointer" onClick={() => toggleInstrument(instrument)}>
                      {instrument}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Vincular ao Membro</Label>
              <Select 
                value={formData.member_id} 
                onValueChange={(value) => {
                  const selectedMember = churchMembers.find(m => m.id === value);
                  setFormData({ 
                    ...formData, 
                    member_id: value,
                    member_name: selectedMember?.full_name || '',
                    full_name: selectedMember?.full_name || formData.full_name,
                    phone: selectedMember?.phone || formData.phone,
                    email: selectedMember?.email || formData.email
                  });
                }}
              >
                <SelectTrigger>
                  {selectedChurchMember ? (
                    <div className="flex items-center gap-2">
                      {selectedChurchMember.photo_url ? (
                        <img
                          src={selectedChurchMember.photo_url}
                          alt={selectedChurchMember.full_name}
                          className="w-6 h-6 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center text-xs font-semibold">
                          {selectedChurchMember.full_name?.charAt(0)?.toUpperCase() || 'M'}
                        </div>
                      )}
                      <span className="truncate">{selectedChurchMember.full_name}</span>
                    </div>
                  ) : (
                    <SelectValue placeholder="Selecione um membro" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>Nenhum</SelectItem>
                  {sortedChurchMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-2">
                        {member.photo_url ? (
                          <img
                            src={member.photo_url}
                            alt={member.full_name}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center text-xs font-semibold">
                            {member.full_name?.charAt(0)?.toUpperCase() || 'M'}
                          </div>
                        )}
                        <span>{member.full_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingMember ? 'Salvar' : 'Cadastrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
